<template>
  <div class="payment-create main-content">
    <div class="page-header">
      <h3 class="page-title">{{ $t("general.add_invoice_information") }}</h3>
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="dashboard">
            {{ $t("general.home") }}
          </router-link>
        </li>
        <li v-if="userType === 'supplier'" class="breadcrumb-item">
          <router-link slot="item-title" to="/supplier/funders">
            {{ $tc("funders.finance", 2) }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link
            v-if="userType === 'supplier'"
            slot="item-title"
            :to="{
              name: 'FunderFinance',
              params: { id: this.$route.params.funder_id }
            }"
          >
            {{ $t("islamic_finance.title") }}
          </router-link>
          <router-link
            v-else-if="userType == 'funder'"
            slot="item-title"
            to="/funder/islamic-finances"
          >
            {{ $t("islamic_finance.title") }}
          </router-link>
        </li>
        <li class="breadcrumb-item">
          <router-link slot="item-title" to="#">
            {{ $t("general.add_invoice_information") }}
          </router-link>
        </li>
      </ol>
      <div class="page-actions row">
        <div class="col-xs-2 mr-4">
          <base-button
            :loading="isLoading"
            :disabled="isLoading"
            size="large"
            icon="save"
            color="theme"
            @click="saveInvoiceInfo"
          >
            {{ $t("islamic_finance.save_invoice_information") }}
          </base-button>
        </div>
        <div class="col-xs-2 mr-4">
          <base-button
            :outline="true"
            size="large"
            icon="plus"
            color="theme"
            @click="addAttachment"
          >
            {{ $t("invoices.newAttachment") }}
          </base-button>
        </div>
      </div>
    </div>
    <single-funder-row
      v-if="userType == 'supplier'"
      :fetch-data="fetchDataFunder"
    />
    <single-islamic-finance-row :fetch-data="fetchData" />
    <div class="payment-card card">
      <div class="card-body">
        <div class="row">
          <div class="col-sm-6">
            <div class="form-group">
              <label class="form-label">{{
                $t("invoices.invoice_number")
              }}</label
              ><span class="text-danger"> *</span>
              <base-input
                v-model="newInvoice.reference_number"
                :invalid="$v.newInvoice.reference_number.$error"
                icon="hashtag"
                @input="$v.newInvoice.reference_number.$touch()"
              />

              <div v-if="$v.newInvoice.reference_number.$error">
                <span v-if="!$v.newInvoice.reference_number.required" class="text-danger">
                  {{ $t("validation.required") }}
                </span>
                <span v-if="!$v.newInvoice.reference_number.maxLength" class="text-danger">
                  {{ $tc("validation.ref_number_maxlength") }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-show="!showEmptyScreen" class="table-container">
      <div class="table-actions mt-5">
        <p class="table-stats">
          {{ $t("general.showing") }}: <b>{{ attachments.length }}</b>
        </p>

        <transition name="fade">
          <v-dropdown v-if="selectedAttachments.length" :show-arrow="false">
            <span
              slot="activator"
              href="#"
              class="table-actions-button dropdown-toggle"
            >
              {{ $t("general.actions") }}
            </span>
          </v-dropdown>
        </transition>
      </div>

      <div class="custom-control custom-checkbox">
        <input
          id="select-all"
          v-model="selectAllFieldStatus"
          type="checkbox"
          class="custom-control-input"
          @change="selectAllAttachments"
        >
        <label for="select-all" class="custom-control-label selectall">
          <span class="select-all-label">{{ $t("general.select_all") }} </span>
        </label>
      </div>

      <table-component
        ref="table"
        :show-filter="false"
        :data="attachments"
        table-class="table"
      >
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="no-click"
        >
          <template slot-scope="row">
            <div class="custom-control custom-checkbox">
              <input
                :id="row.id"
                v-model="selectField"
                :value="row.id"
                type="checkbox"
                class="custom-control-input"
              >
              <label :for="row.id" class="custom-control-label" />
            </div>
          </template>
        </table-column>
        <table-column
          :label="$tc('documents.documentType', 1)"
          show="documentType.name"
        />
        <table-column :label="$t('documents.document')" show="name" />
        <table-column
          :sortable="false"
          :filterable="false"
          cell-class="action-dropdown"
        >
          <template slot-scope="row">
            <span> {{ $t("customers.action") }} </span>
            <v-dropdown>
              <a slot="activator" href="#"><dot-icon /></a>
              <v-dropdown-item>
                <div class="dropdown-item">
                  <a v-if="row.path" :href="row.path" target="_blank">
                    <font-awesome-icon icon="eye" class="dropdown-item-icon" />
                    {{ $t("general.view") }}
                  </a>
                </div>
              </v-dropdown-item>
            </v-dropdown>
          </template>
        </table-column>
      </table-component>
    </div>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import MultiSelect from 'vue-multiselect'
import { validationMixin } from 'vuelidate'
import SingleIslamicFinanceRow from './SingleIslamicFinanceRow.vue'
import SingleFunderRow from '../funders/SingleFunderRow.vue'
const {
  required,
  between,
  maxLength,
  numeric
} = require('vuelidate/lib/validators')

export default {
  components: {
    MultiSelect,
    SingleIslamicFinanceRow,
    SingleFunderRow
  },
  mixins: [validationMixin],
  computed: {
    ...mapGetters('OfferToSell', [
      'offerToSells',
      'selectedOfferToSells',
      'totalOfferToSells',
      'selectAllFieldAttachment',
      'attachments',
      'totalAttachments',
      'selectedAttachments'
    ]),
    ...mapGetters('islamicFinanceForm', ['islamicFinanceForm']),
    showEmptyScreen () {
      return !this.totalAttachments && !this.isRequestOngoing
    },
    offerAcceptForm () {
      return this.islamicFinanceForm.find(x => x.name === 'Offer Accepted')
    },
    selectField: {
      get: function () {
        return this.selectedAttachments
      },
      set: function (val) {
        this.selectAttachment(val)
      }
    },
    selectAllFieldStatus: {
      get: function () {
        return this.selectAllFieldAttachment
      },
      set: function (val) {
        this.setSelectAllState(val)
      }
    }
  },
  data () {
    return {
      userType: null,
      fetchData: [],
      actionRecord: [],
      fetchDataFunder: [],
      isLoading: false,
      newInvoice: {
        reference_number: null
      }
    }
  },
  validations () {
    return {
      newInvoice: {
        reference_number: {
          required,
          maxLength: maxLength(255)
        }
      }
    }
  },
  created () {
    if (localStorage.getItem('role') === 'super_admin') {
      this.userType = 'super_admin'
    } else if (localStorage.getItem('account_type') === 'anchor') {
      this.userType = 'anchor'
    } else if (localStorage.getItem('account_type') === 'supplier') {
      this.userType = 'supplier'
      this.loadFunder()
    } else if (localStorage.getItem('account_type') === 'funder') {
      this.userType = 'funder'
    }
    this.loadFinance()
    this.Attachments()
  },
  methods: {
    ...mapActions('OfferToSell', [
      'SingleIslamicFinanceArray',
      'deleteAttachment',
      'fetchAttachments',
      'selectAllAttachments',
      'setSelectAllState',
      'selectAttachment',
      'deleteMultipleAttachments',
      'markAsInvoiceInformationAdded'
    ]),
    ...mapActions('funder', ['singleFunderArray']),
    ...mapActions('modal', ['openModal']),
    async Attachments () {
      const data = { islamic_finance_id: this.$route.params.id }
      let response = await this.fetchAttachments(data)
      this.isRequestOngoing = false
      return response.data
    },

    async saveInvoiceInfo () {
      this.$v.newInvoice.$touch()
      if (this.$v.$invalid) {
        return false
      }
      this.isLoading = true
      const data = {
        islamic_finance_id: this.$route.params.id,
        invoice_number: this.newInvoice.reference_number
      }

      let response = await this.markAsInvoiceInformationAdded(data)
      if (response.data.success) {
        window.toastr['success'](
          this.$t('islamic_finance.invoice_info_successfully')
        )
        this.$router.push(
          `/supplier/funders/${this.$route.params.funder_id}/islamic_finance`
        )
        this.isLoading = false
      } else {
        this.isLoading = false
      }
    },
    async addAttachment () {
      this.openModal({
        title: 'Add Attachment',
        componentName: 'IslamicFinanceAttachmentModal'
      })
    },
    async loadFunder () {
      let response = await this.singleFunderArray(this.$route.params.funder_id)
      this.fetchDataFunder = response.data.account
    },
    async loadFinance () {
      let response = await this.SingleIslamicFinanceArray(
        this.$route.params.id
      )
      if (response.data) {
        this.fetchData = response.data.islamicFinance
        this.actionRecord =
          response.data.islamicFinance[0].islamic_finance_status
      }
    }
  }
}
</script>
<style scoped>
.table-component {
  min-height: 0px !important;
}
</style>
